import React, { useState, forwardRef, useEffect, useCallback, useMemo } from "react";
import { useConfig } from "../../../providers/config";
import { useOrderData } from "../../../providers/orderDataStore";
import useGetDeliveryDatesQuery from "../../../providers/shippingAndPayment/useGetDeliveryDatesQuery";
import useGetDeliveryIntervalsQuery from "../../../providers/shippingAndPayment/useGetDeliveryIntervalsQuery";
import { CustomBottomSheet, CustomBottomSheetRef } from "../../../components/UI/CustomBottomSheet";
import { IonIcon, IonSkeletonText, IonSpinner } from "@ionic/react";

import { ReactComponent as PencilIcon } from "../../../assets/icon/pencil.svg";
import useCheckAddressForExpressMutation from "../../../providers/shippingZones/useGetCheckAddressForExpress";
import useGetCurrentAddress from "../../../hooks/useGetCurrentAddress";
import { useCartData } from "../../../providers/cartDataStore";
import { useMarketMode } from "../../../providers/marketMode";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import dayjs from "dayjs";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { createSafeDate, getSafeISODate } from "../../../utils/dateUtils";
import { generateDeliveryDates } from "../../../utils/deliveryDates";
import { getStaticDeliveryIntervals } from "../../../utils/getStaticDeliveryIntervals";
import { useCustomerAddressesStorageData } from "../../../providers/customerAddressesStorage";
import { refresh } from "ionicons/icons";
import { deliveryDatesClient } from "@core/clients";
import { AppMetricaProvider } from "../../../providers/AppMetrica/AppMetrica";
import { toast } from "react-toastify";

type TDeliveryType = "nearest" | "other" | "express";

export const DeliveryTimeContent = forwardRef<CustomBottomSheetRef>((props, ref) => {
  const config = useConfig();
  const { currentShippingAddressId } = useCustomerAddressesStorageData();
  const { deliveryDate, deliveryInterval, shippingMethod, updateOrderData } = useOrderData();

  const isFutureDate = deliveryDate && !dayjs(deliveryDate).isSame(dayjs(), "day");

  const [deliveryType, setDeliveryType] = useState<TDeliveryType>(isFutureDate ? "other" : "nearest");
  const [hasRetriedWithEmptyResult, setHasRetriedWithEmptyResult] = useState(false);
  const currentAddress = useGetCurrentAddress();
  const { getProductsTotalWeight } = useCartData();
  const {
    data: expressPoints,
    mutateAsync: checkAddressForExpress,
    isPending: isCheckAddressForExpressPending,
    error: checkAddressForExpressError,
  } = useCheckAddressForExpressMutation();
  const { mode, toggleMode } = useMarketMode();
  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();
  const { t } = useTranslation();

  const deliveryDateEnabled = config?.isDeliveryDateEnabled;

  const checkAddressForExpressErrorMessage = checkAddressForExpressError?.message;

  const {
    data: fetchedDeliveryDateList,
    isFetching: isDeliveryDateListFetching,
    isLoading: isDeliveryDateListLoading,
  } = useGetDeliveryDatesQuery(
    { shippingMethodId: shippingMethod?.id },
    {
      enabled: deliveryDateEnabled,
      onSuccess: (data) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isData = data && data.length > 0;
        const isNotDeliveryDateOrToday = !deliveryDate || deliveryDate.getTime() < today.getTime();

        if (isNotDeliveryDateOrToday && isData) {
          const safeDate = createSafeDate(data[0]);
          if (safeDate) {
            updateOrderData({ deliveryDate: safeDate });
          }
        }
      },
    },
  );

  const deliveryDateList = fetchedDeliveryDateList ?? generateDeliveryDates();
  const date = getSafeISODate(deliveryDate);

  const deliveryIntervalEnabled = config?.isDeliveryIntervalEnabled && deliveryDateEnabled && !!date;

  const {
    data: fetchedDeliveryIntervals,
    isFetching: isDeliveryIntervalsFetching,
    isLoading: isIntervalsLoading,
    refetch: refetchDeliveryIntervals,
  } = useGetDeliveryIntervalsQuery(
    {
      date,
    },
    {
      enabled: deliveryIntervalEnabled,
      onSuccess: () => {
        updateOrderData({ deliveryInterval: "" });
      },
    },
  );

  const totalWeight = getProductsTotalWeight();

  const isWeightExceeded = totalWeight > config.expressMaxWeightKg;

  const isWeightNotExceeded = totalWeight <= config.expressMaxWeightKg;

  const isDeliveryDateLoading = deliveryDateEnabled && (isDeliveryDateListFetching || isDeliveryDateListLoading);
  const isDeliveryIntervalsLoading = deliveryIntervalEnabled && (isDeliveryIntervalsFetching || isIntervalsLoading);

  const deliveryIntervals = fetchedDeliveryIntervals ?? getStaticDeliveryIntervals(getSafeISODate(deliveryDate));
  const chosenInterval = deliveryIntervals?.find((interval) => interval.text === deliveryInterval);

  const hasNoIntervals = !fetchedDeliveryIntervals || fetchedDeliveryIntervals.length === 0;
  const isNotLoading = !isDeliveryIntervalsLoading && !isDeliveryIntervalsFetching;

  const shouldShowRetryButton = hasNoIntervals && isNotLoading && !hasRetriedWithEmptyResult;
  const shouldShowNoIntervalsMessage = hasNoIntervals && isNotLoading && hasRetriedWithEmptyResult;

  const handleRetryWithEmptyCheck = useCallback(async () => {
    try {
      const date = getSafeISODate(deliveryDate);
      const intervals = await deliveryDatesClient?.getDeliveryIntervals(shippingMethod?.id, date);

      if (!intervals?.length) {
        setHasRetriedWithEmptyResult(true);

        setTimeout(() => {
          setDeliveryType("other");
          mode === "express" && toggleMode("market", activeShippingZone?.id);

          if (deliveryDateList && deliveryDateList.length > 1) {
            const nextDay = deliveryDateList[1];
            const safeDate = createSafeDate(nextDay);
            if (safeDate) {
              updateOrderData({ deliveryDate: safeDate, deliveryInterval: "" });
            }
          }
          setHasRetriedWithEmptyResult(false);
        }, 1500);
      }
    } catch (e) {
      toast.error(t("Error getting delivery intervals"));
      console.error(e);
      if (e instanceof Error)
        AppMetricaProvider.reportError("handleRetryWithEmptyCheck error", `${e.message} ${e.stack}`);
    }
  }, [refetchDeliveryIntervals, deliveryDateList, updateOrderData, deliveryDate]);

  const handleUpdateDeliveryDate = (date: Date) => {
    const safeDate = createSafeDate(date);
    if (safeDate) {
      updateOrderData({ deliveryDate: safeDate });
      mode === "express" && toggleMode("market", activeShippingZone?.id);
    }
  };

  const handleUpdateDeliveryInterval = (interval: string) => {
    updateOrderData({ deliveryInterval: interval });
    mode === "express" && toggleMode("market", activeShippingZone?.id);
  };

  const handleDeliveryTypeChange = useCallback(
    (type: TDeliveryType) => {
      if (type === deliveryType) return;

      if (type === "express") {
        toggleMode("express", activeShippingZone?.id);
      } else if (deliveryType === "express") {
        toggleMode("market", activeShippingZone?.id);
      }

      setDeliveryType(type);

      if (type === "other") {
        const safeDate = createSafeDate(deliveryDateList?.[0]);
        const safeDeliveryDate = safeDate || new Date();
        updateOrderData({
          deliveryDate: safeDeliveryDate,
          deliveryInterval: deliveryIntervals?.[0]?.text || "",
        });
      } else if (type === "nearest") {
        const safeDate = createSafeDate(deliveryDateList?.[0]);
        if (safeDate) {
          updateOrderData({ deliveryDate: safeDate });
        }
      }
    },
    [activeShippingZone?.id, deliveryDateList, deliveryIntervals, deliveryType, toggleMode, updateOrderData],
  );

  const checkExpressAvailability = useCallback(async () => {
    if (isWeightExceeded || (deliveryType === "express" && mode === "express")) {
      setDeliveryType("nearest");
      toggleMode("market", activeShippingZone?.id);
      return;
    }

    if (currentAddress && currentAddress.latitude && currentAddress.longitude) {
      try {
        const data = await checkAddressForExpress({
          latitude: currentAddress.latitude,
          longitude: currentAddress.longitude,
          totalWeight,
        });

        const isHaveExpressPoints = data && data.expressPoints.length > 0;

        if (isHaveExpressPoints) {
          setDeliveryType("express");
          toggleMode("express", activeShippingZone?.id);
          const safeDate = createSafeDate(deliveryDateList?.[0]);
          const safeDeliveryDate = safeDate || new Date();
          updateOrderData({ deliveryDate: safeDeliveryDate, deliveryInterval: undefined });
        } else {
          setDeliveryType("nearest");
          toggleMode("market", activeShippingZone?.id);
        }
      } catch (error) {
        setDeliveryType("nearest");
        toggleMode("market", activeShippingZone?.id);
      }
    }
  }, [
    mode,
    toggleMode,
    deliveryType,
    currentAddress,
    updateOrderData,
    deliveryDateList,
    isWeightExceeded,
    activeShippingZone?.id,
    checkAddressForExpress,
    totalWeight,
  ]);

  useEffect(() => {
    if (config.isExpressDeliveryEnabled) {
      checkExpressAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAddress, config.isExpressDeliveryEnabled]);

  const isDeliveryTypeExpressAndAvailable =
    config.isExpressDeliveryEnabled && deliveryType === "express" && mode === "express";

  const isCheckAddressForExpressDisabled = isCheckAddressForExpressPending || isWeightExceeded;

  const renderExpressButtonContent = () => {
    if (!currentShippingAddressId) {
      return t("Select address for express check delivery");
    }

    if (isWeightExceeded) {
      return t("Too heavy for express");
    }

    if (isCheckAddressForExpressPending) {
      return (
        <>
          {t("Express") + " ⚡"}
          <IonSpinner className="w-4 h-4" />
        </>
      );
    }

    if (checkAddressForExpressErrorMessage) {
      return checkAddressForExpressErrorMessage;
    }

    if (expressPoints) {
      if (expressPoints.expressPoints.length > 0) {
        return t("Express") + " ⚡";
      } else {
        return t("Too far for express");
      }
    }
    // Initial state before first check
    return t("Express") + " ⚡";
  };

  const futureDateText =
    dayjs(`${deliveryDate}`).format("DD.MM") + (chosenInterval?.text ? `, ${chosenInterval?.text}` : "");

  const deliveryDateText =
    deliveryType === "express" && mode === "express"
      ? t("Express")
      : isFutureDate
        ? futureDateText
        : chosenInterval?.text || "";

  const shouldShowOtherDeliveryDate = !isDeliveryDateLoading && deliveryType === "other";

  return (
    <CustomBottomSheet
      ref={ref}
      title={t("Delivery date")}
      textForDefaultPreview={deliveryDateText}
      descForDefaultPreview={deliveryDateText}
      snapPoints={({ minHeight }) => [minHeight]}
      footerTitle={t("Continue")}
      renderPreview={() => (
        <div className="flex relative items-center justify-between px-2.5 py-4 border-t border-b min-h-[4.375rem]">
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className=" text-[#292929]">{t("Choose delivery time")}</span>
              <span className="text-xs text-red-500">*</span>
            </div>
            <div className="text-xs text-[#656565]">{deliveryDateText}</div>
          </div>
          <PencilIcon className="text-xs font-bold w-4 h-4 text-[#D5D5D5]" />
        </div>
      )}
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-2 rounded-xl p-1 bg-gray-100">
          {config.isExpressDeliveryEnabled && (
            <button
              className={cx(
                "flex items-center justify-center gap-1 flex-1 py-2 rounded-xl disabled:opacity-40",
                isDeliveryTypeExpressAndAvailable && "font-semibold bg-green-500 text-white",
                isCheckAddressForExpressDisabled && "text-sm",
              )}
              onClick={checkExpressAvailability}
              type="button"
              disabled={isCheckAddressForExpressDisabled}
            >
              {renderExpressButtonContent()}
            </button>
          )}
          <div className="flex items-center">
            {config.isExpressDeliveryEnabled && deliveryType === "express" ? (
              <button
                type="button"
                className={cx("flex-1 py-2 rounded-xl")}
                onClick={() => handleDeliveryTypeChange("nearest")}
              >
                {t("Specify another time")}
              </button>
            ) : (
              <>
                <button
                  className={cx("flex-1 py-2 rounded-xl", deliveryType === "nearest" && "bg-[#FFE145]")}
                  type="button"
                  onClick={() => handleDeliveryTypeChange("nearest")}
                >
                  {t("Nearest")}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeliveryTypeChange("other")}
                  className={cx("flex-1 py-2 rounded-xl", deliveryType === "other" && "bg-[#FFE145]")}
                >
                  {t("Other")}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={cx("flex flex-col", deliveryType === "other" && "gap-4")}>
          <div
            className={cx("w-full touch-action-pan-x", {
              "border-b-1 pb-4 border-gray-300": deliveryType === "other",
            })}
          >
            <div className="overflow-x-auto whitespace-nowrap no-scrollbar flex space-x-2 pb-2">
              {shouldShowOtherDeliveryDate &&
                deliveryDateList?.map((date) => {
                  const selected = date === dayjs(`${deliveryDate}`).format("YYYY-MM-DD");
                  return (
                    <div
                      key={date}
                      className={cx("border flex justify-center rounded-xl border-gray-400 text-gray-500", {
                        "font-bold border-green-700 text-green-700": selected,
                      })}
                    >
                      <button
                        type="button"
                        onClick={() => handleUpdateDeliveryDate(date as unknown as Date)}
                        className="px-4 py-2 rounded-lg"
                      >
                        {dayjs(`${date}`).format("dd DD.MM")}
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="flex justify-center">
            {isDeliveryTypeExpressAndAvailable && isWeightNotExceeded ? (
              <div
                className={cx(
                  "py-3 px-4 border flex flex-col items-center rounded-xl font-bold border-green-700 text-green-700",
                )}
              >
                {t("We will deliver in an hour")}
              </div>
            ) : shouldShowNoIntervalsMessage ? (
              <div className="flex flex-col items-center gap-3 py-3 px-4">
                <span className="text-gray-600 text-center">{t("No available intervals for today")}</span>
                <IonSpinner className="w-5 h-5" />
              </div>
            ) : shouldShowRetryButton ? (
              <button
                onClick={handleRetryWithEmptyCheck}
                className="flex items-center gap-2 py-3 px-4 border border-solid rounded-xl border-gray-400 text-gray-500 hover:bg-gray-50"
              >
                <IonIcon icon={refresh} className="w-5 h-5" />
                <span>{t("Retry")}</span>
              </button>
            ) : (
              <div
                className={cx(
                  "grid gap-3",
                  ((Array.isArray(deliveryIntervals) && deliveryIntervals.length > 1) || isDeliveryIntervalsLoading) &&
                    "grid-cols-2 ",
                )}
              >
                {isDeliveryIntervalsLoading
                  ? Array.from({ length: 7 }).map((_, index) => (
                      <IonSkeletonText key={index} className="m-0 py-5 px-14 rounded-xl" />
                    ))
                  : deliveryIntervals?.map((slot) => {
                      const selected = slot.text === deliveryInterval;
                      return (
                        <div key={slot.id}>
                          {isDeliveryIntervalsFetching ? (
                            <IonSkeletonText className="w-[7.878125rem] h-[3.125rem] rounded-xl" animated />
                          ) : (
                            <button
                              onClick={() => handleUpdateDeliveryInterval(slot.text)}
                              disabled={isDeliveryTypeExpressAndAvailable}
                            >
                              <div
                                className={cx(
                                  "py-3 px-4 border flex flex-col items-center rounded-xl border-gray-400 text-gray-500",
                                  {
                                    "font-bold border-green-700 text-green-700": selected,
                                    "opacity-40": isDeliveryTypeExpressAndAvailable,
                                  },
                                )}
                              >
                                {slot.text}
                              </div>
                            </button>
                          )}
                        </div>
                      );
                    })}
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomBottomSheet>
  );
});

export default DeliveryTimeContent;
